import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { formatDateTime } from '../../../../lib/date/formatDateTime';
import { usePermissions } from '../../../permissions/usePermissions';
import type { ChannelState } from '../../types/gen-file.types';
import theme from '../../../../theme/theme';
import {
  GEN_COLORS,
  GEN_CARD,
  CHANNEL_STYLES,
  STATUS_COLORS,
  BAR_COLORS,
  CALLOUT_STYLES,
  MONOSPACE_FONT,
  type ChannelType,
  type DiffusionStatus,
} from '../../constants/gen-colors';

const { font, spacing, size } = GEN_CARD;

export interface DiffusionTally {
  ok: number;
  pending: number;
  gap: number;
  never: number;
  fail: number;
}

interface GeneralChannelCardProps {
  channel: ChannelType;
  channelState: ChannelState | null;
  onUpload: () => void;
  countryCount?: number;
  instanceCount?: number;
  tally?: DiffusionTally;
}

function StatusTag({ status, label }: { status: DiffusionStatus; label: string }) {
  const cfg = STATUS_COLORS[status];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: theme.chip.sm,
        px: spacing.chipPx,
        borderRadius: theme.borderRadius.pill,
        bgcolor: cfg.bg,
        border: `1px solid ${cfg.border}`,
        fontSize: font.sizeTag,
        fontWeight: theme.typography.fontWeightSemiBold,
        color: cfg.color,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}

function DiffusionBar({ segments }: { segments: { key: DiffusionStatus; pct: number }[] }) {
  return (
    <Box
      sx={{
        height: size.barHeight,
        borderRadius: theme.borderRadius.pill,
        bgcolor: GEN_COLORS.offBg,
        overflow: 'hidden',
        display: 'flex',
        mt: spacing.chipPx,
      }}
    >
      {segments
        .filter(s => s.pct > 0)
        .map(s => (
          <Box
            key={s.key}
            sx={{ height: '100%', width: `${s.pct}%`, bgcolor: BAR_COLORS[s.key] }}
          />
        ))}
    </Box>
  );
}

function Callout({
  variant,
  icon,
  children,
}: {
  variant?: 'warn' | 'danger';
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const style = variant ? CALLOUT_STYLES[variant] : null;
  return (
    <Box
      sx={{
        display: 'flex',
        gap: spacing.gap,
        p: spacing.calloutPadding,
        borderRadius: theme.borderRadius.xs,
        fontSize: font.sizeBody,
        lineHeight: font.lineHeightRelaxed,
        border: `1px solid ${style ? style.border : GEN_COLORS.border}`,
        bgcolor: style ? style.bg : theme.palette.background.section,
        color: style ? style.color : theme.palette.text.muted,
        '& b': { color: style ? style.bold : theme.palette.text.body },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          mt: spacing.calloutIconMt,
          width: size.calloutIconBox,
          height: size.calloutIconBox,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

const DEFAULT_TALLY: DiffusionTally = { ok: 0, pending: 0, gap: 0, never: 0, fail: 0 };

export function GeneralChannelCard({
  channel,
  channelState,
  onUpload,
  countryCount = 0,
  instanceCount = 0,
  tally = DEFAULT_TALLY,
}: GeneralChannelCardProps) {
  const { t } = useTranslation('generalSettings');
  const { can } = usePermissions();
  const canModify = can('general_settings', 'modify');
  const isGeneral = channel === 'general';

  const channelStyle = CHANNEL_STYLES[channel];
  const current = channelState?.current ?? null;

  const total = instanceCount;

  const barSegments: { key: DiffusionStatus; pct: number }[] =
    total > 0
      ? [
          { key: 'ok', pct: (tally.ok / total) * 100 },
          { key: 'pending', pct: (tally.pending / total) * 100 },
          { key: 'gap', pct: (tally.gap / total) * 100 },
          { key: 'fail', pct: (tally.fail / total) * 100 },
        ]
      : [];

  const legend: { status: DiffusionStatus; count: number }[] = [];
  if (tally.ok) legend.push({ status: 'ok', count: tally.ok });
  if (tally.pending) legend.push({ status: 'pending', count: tally.pending });
  if (tally.gap) legend.push({ status: 'gap', count: tally.gap });
  if (tally.never) legend.push({ status: 'never', count: tally.never });
  if (tally.fail) legend.push({ status: 'fail', count: tally.fail });

  const outOfVersion = tally.gap + tally.never;

  return (
    <Box
      sx={{
        bgcolor: theme.palette.common.white,
        border: `1px solid ${GEN_COLORS.border}`,
        borderRadius: theme.borderRadius.sm,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        borderTop: `${size.stripWidth}px solid ${channelStyle.stripColor}`,
      }}
    >
      {/* Card header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: spacing.gap,
          p: spacing.headerPadding,
          borderBottom: `1px solid ${GEN_COLORS.border}`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h2"
            sx={{
              fontSize: theme.typography.fontSizeTitle,
              fontWeight: theme.typography.fontWeightSemiBold,
              lineHeight: font.lineHeightTitle,
              color: theme.palette.text.body,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {isGeneral ? t('card.title') : t('card.titlePilote')}
            <Chip
              size="small"
              label={isGeneral ? t('card.tagGeneral') : t('card.tagPilote')}
              sx={{
                height: theme.chip.sm,
                fontSize: font.sizeTag,
                fontWeight: theme.typography.fontWeightSemiBold,
                bgcolor: channelStyle.tagBg,
                border: `1px solid ${channelStyle.tagBorder}`,
                color: channelStyle.tagColor,
                borderRadius: theme.borderRadius.pill,
              }}
            />
          </Typography>
          <Typography
            sx={{
              fontSize: font.sizeBody,
              lineHeight: font.lineHeightNormal,
              color: theme.palette.text.muted,
              mt: spacing.scopeMt,
            }}
          >
            {isGeneral ? t('card.scopeGeneral') : t('card.scopePilote')}
          </Typography>
        </Box>
      </Box>

      {/* Card body */}
      {!current ? (
        <Box sx={{ p: spacing.emptyPadding, textAlign: 'center', flex: 1 }}>
          <Box
            sx={{
              width: size.emptyIconBox,
              height: size.emptyIconBox,
              borderRadius: theme.borderRadius.sm,
              bgcolor: theme.palette.background.section,
              border: `1px solid ${GEN_COLORS.border}`,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.text.subtle,
              mb: spacing.emptyIconMb,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: size.iconEmpty }} />
          </Box>
          <Typography
            sx={{
              fontSize: theme.typography.fontSizeDescription,
              fontWeight: theme.typography.fontWeightSemiBold,
              color: theme.palette.text.body,
            }}
          >
            {t('card.empty')}
          </Typography>
          <Typography
            sx={{
              fontSize: font.sizeBody,
              lineHeight: font.lineHeightRelaxed,
              color: theme.palette.text.muted,
              mt: spacing.emptyDescMt,
              maxWidth: size.emptyDescMaxWidth,
              mx: 'auto',
            }}
          >
            {isGeneral ? t('card.emptyDescGeneral') : t('card.emptyDescPilote')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ p: spacing.bodyPadding, flex: 1 }}>
          {/* Version block */}
          <Typography
            sx={{
              fontSize: font.sizeTag,
              fontWeight: theme.typography.fontWeightSemiBold,
              letterSpacing: font.letterSpacingLabel,
              textTransform: 'uppercase',
              color: theme.palette.text.subtle,
              mb: spacing.versionLabelMb,
            }}
          >
            {t('card.version')}
          </Typography>
          <Typography
            sx={{
              fontSize: font.sizeVersion,
              fontWeight: theme.typography.fontWeightSemiBold,
              fontFamily: MONOSPACE_FONT,
              color: theme.palette.text.body,
              letterSpacing: font.letterSpacingVersion,
              lineHeight: font.lineHeightTight,
            }}
          >
            {current.version}
          </Typography>
          <Typography
            sx={{
              fontSize: font.sizeBody,
              lineHeight: font.lineHeightRelaxed,
              color: theme.palette.text.muted,
              mt: spacing.publishedMt,
            }}
          >
            {t('card.publishedOn')} {formatDateTime(current.uploaded_at)} {t('card.publishedBy')}{' '}
            {current.uploaded_by.username ?? t('card.deletedUser')}
          </Typography>

          {/* KPIs */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: spacing.gap,
              mt: spacing.section,
            }}
          >
            <Box
              sx={{
                bgcolor: theme.palette.background.section,
                border: `1px solid ${GEN_COLORS.border}`,
                borderRadius: theme.borderRadius.xs,
                p: spacing.kpiPadding,
              }}
            >
              <Typography
                sx={{
                  fontSize: font.sizeKpi,
                  fontWeight: theme.typography.fontWeightSemiBold,
                  color: theme.palette.text.body,
                  lineHeight: font.lineHeightTight,
                }}
              >
                {countryCount}
              </Typography>
              <Typography
                sx={{
                  fontSize: font.sizeSmall,
                  color: theme.palette.text.muted,
                  mt: spacing.kpiLabelMt,
                }}
              >
                {t('card.kpi.countries')}
              </Typography>
            </Box>
            <Box
              sx={{
                bgcolor: theme.palette.background.section,
                border: `1px solid ${GEN_COLORS.border}`,
                borderRadius: theme.borderRadius.xs,
                p: spacing.kpiPadding,
              }}
            >
              <Typography
                sx={{
                  fontSize: font.sizeKpi,
                  fontWeight: theme.typography.fontWeightSemiBold,
                  color: theme.palette.text.body,
                  lineHeight: font.lineHeightTight,
                }}
              >
                {instanceCount}
              </Typography>
              <Typography
                sx={{
                  fontSize: font.sizeSmall,
                  color: theme.palette.text.muted,
                  mt: spacing.kpiLabelMt,
                }}
              >
                {t('card.kpi.instances')}
              </Typography>
            </Box>
          </Box>

          {/* Diffusion section */}
          <Box
            sx={{
              mt: spacing.section,
              pt: spacing.diffusionPt,
              borderTop: `1px solid ${GEN_COLORS.border}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography
                sx={{
                  fontSize: font.sizeBody,
                  fontWeight: theme.typography.fontWeightSemiBold,
                  color: theme.palette.text.body,
                  flex: 1,
                }}
              >
                {t('card.diffusion.title')}
              </Typography>
              <Typography
                sx={{
                  fontSize: font.sizeBody,
                  fontWeight: theme.typography.fontWeightSemiBold,
                  color: theme.palette.text.muted,
                }}
              >
                {t('card.diffusion.value', { ok: tally.ok, total })}
              </Typography>
            </Box>
            <DiffusionBar segments={barSegments} />
            {legend.length > 0 && (
              <Stack
                direction="row"
                spacing={0.75}
                sx={{ flexWrap: 'wrap', mt: spacing.legendMt, gap: spacing.gapSm }}
              >
                {legend.map(l => (
                  <StatusTag
                    key={l.status}
                    status={l.status}
                    label={`${l.count} ${t(`card.statusTag.${l.status}`)}`}
                  />
                ))}
              </Stack>
            )}
          </Box>

          {/* Alert callouts */}
          {tally.pending > 0 && (
            <Box sx={{ mt: spacing.calloutMt }}>
              <Callout icon={<InfoOutlinedIcon sx={{ fontSize: size.iconCallout }} />}>
                <b>
                  {t(tally.pending > 1 ? 'card.alert.pending_plural' : 'card.alert.pending', {
                    count: tally.pending,
                  })}
                </b>
              </Callout>
            </Box>
          )}
          {tally.fail > 0 && (
            <Box sx={{ mt: spacing.calloutMt }}>
              <Callout
                variant="danger"
                icon={<ErrorOutlineIcon sx={{ fontSize: size.iconCallout }} />}
              >
                <b>
                  {t(tally.fail > 1 ? 'card.alert.fail_plural' : 'card.alert.fail', {
                    count: tally.fail,
                  })}
                </b>
              </Callout>
            </Box>
          )}
          {outOfVersion > 0 && tally.pending === 0 && tally.fail === 0 && (
            <Box sx={{ mt: spacing.calloutMt }}>
              <Callout
                variant="warn"
                icon={<WarningAmberOutlinedIcon sx={{ fontSize: size.iconCallout }} />}
              >
                <b>
                  {t(
                    outOfVersion > 1
                      ? 'card.alert.outOfVersion_plural'
                      : 'card.alert.outOfVersion',
                    { count: outOfVersion },
                  )}
                </b>
              </Callout>
            </Box>
          )}
        </Box>
      )}

      {/* Card footer */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.gap,
          p: spacing.footerPadding,
          borderTop: `1px solid ${GEN_COLORS.border}`,
          bgcolor: theme.palette.background.section,
          mt: current ? spacing.calloutMt : 0,
        }}
      >
        {canModify && (
          <Button
            variant="contained"
            size="small"
            startIcon={<UploadFileOutlinedIcon sx={{ fontSize: size.iconUpload }} />}
            onClick={onUpload}
            sx={{
              bgcolor: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              borderRadius: theme.borderRadius.xs,
              textTransform: 'none',
              fontSize: font.sizeBody,
              fontWeight: theme.typography.fontWeightSemiBold,
              height: size.buttonHeight,
              px: spacing.buttonPx,
              '&:hover': {
                bgcolor: GEN_COLORS.primaryText,
                borderColor: GEN_COLORS.primaryText,
              },
            }}
          >
            {t('card.upload')}
          </Button>
        )}
        {!isGeneral && (
          <Typography
            component="a"
            href="#"
            onClick={(e: React.MouseEvent) => e.preventDefault()}
            sx={{
              fontSize: font.sizeBody,
              fontWeight: theme.typography.fontWeightSemiBold,
              color: theme.palette.primary.main,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {t('card.modifySelection')}
          </Typography>
        )}
      </Box>

      {/* Last upload failed alert */}
      {channelState?.last_upload &&
        channelState.last_upload.integration_status === 'failed' &&
        (!current || channelState.last_upload.version !== current.version) && (
          <Alert severity="error" sx={{ mx: spacing.alertInset, mb: spacing.calloutMt }}>
            <Typography
              sx={{
                fontWeight: theme.typography.fontWeightSemiBold,
                fontSize: theme.typography.fontSizeDescription,
              }}
            >
              {t('card.lastUploadFailed')}
            </Typography>
            {channelState.last_upload.integration_error && (
              <Typography sx={{ fontSize: theme.typography.fontSizeDescription, mt: 0.5 }}>
                {channelState.last_upload.integration_error}
              </Typography>
            )}
          </Alert>
        )}
    </Box>
  );
}
